import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Alert,
  Snackbar,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import { Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import {
  checkFreeDealers,
  sendShiftChnageRequest,
  dealerChnageShift,
  shiftActions,
} from "store/slice/shiftsSlice";
import { position } from "stylis";
import { getShiftLabel } from "utilities";
import { getPartLabel } from "utilities";

const ChangeShiftForm = ({
  shiftsStore,
  requestedShift,
  availableDealers,
  hasChecked,
  handleModalClose,
  formSuccessMessage
}) => {
  const dispatch = useDispatch();
  const {
    values,
    errors,
    touched,
    setFieldValue,
    handleChange,
    handleSubmit,
    isSubmitting,
  } = useFormikContext();

  useEffect(() => {
    dispatch(shiftActions.resetCheckDealers());
    setFieldValue("selectedDealerId", "");
  }, [
    values.selectedDate,
    values.changingPart,
    values.selectedShift,
    values.shiftMode,
    values.partTimePart,
    values.actionType,
  ]);

  return (
    <>
      <DialogContent>
        {requestedShift && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Requested Shift: <b>{format(new Date(requestedShift.day), "dd MMM yyyy")}</b> - 
            {" "}{getShiftLabel(requestedShift.shift)}
          </Alert>
        )}
          {formSuccessMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {formSuccessMessage}
          </Alert>
        )}
        {shiftsStore.error.status && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {shiftsStore.error.message}
          </Alert>
        )}

        <FormControl component="fieldset" margin="normal" fullWidth>
          <Typography variant="subtitle1">Select Action</Typography>
          <RadioGroup
            row
            name="actionType"
            value={values.actionType}
            onChange={handleChange}
          >
            <FormControlLabel value="change" control={<Radio />} label="Change Shift" />
            <FormControlLabel value="remove" control={<Radio />} label="Remove Shift" />
          </RadioGroup>
        </FormControl>

        {values.actionType === "change" && (
          <>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Select Date"
                value={values.selectedDate}
                onChange={(value) => setFieldValue("selectedDate", value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    margin="normal"
                    error={Boolean(touched.selectedDate && errors.selectedDate)}
                    helperText={touched.selectedDate && errors.selectedDate}
                  />
                )}
                PopperProps={{
                  sx: {
                    zIndex: 9999999, // Higher than Dialog (1300 by default)
                  },
                }}
              />
            </LocalizationProvider>

            <FormControl component="fieldset" margin="normal" fullWidth>
              <Typography variant="subtitle1">Select Shift Type</Typography>
              <RadioGroup
                name="selectedShift"
                value={values.selectedShift}
                onChange={handleChange}
              >
                <FormControlLabel value="1" control={<Radio />} label="Morning" />
                <FormControlLabel value="2" control={<Radio />} label="Night" />
              </RadioGroup>
            </FormControl>

            <FormControl component="fieldset" margin="normal">
              <Typography variant="subtitle1">Shift Mode</Typography>
              <RadioGroup
                row
                name="shiftMode"
                value={values.shiftMode}
                onChange={handleChange}
              >
                <FormControlLabel value="1" control={<Radio />} label="Full Time" />
                <FormControlLabel value="2" control={<Radio />} label="Part Time" />
              </RadioGroup>
            </FormControl>

            {values.shiftMode === "2" && (
              <>
                <FormControl component="fieldset" margin="normal" fullWidth>
                  <Typography variant="subtitle2">Which part do you want to change?</Typography>
                  <RadioGroup
                    row
                    name="changingPart"
                    value={values.changingPart}
                    onChange={handleChange}
                  >
                    <FormControlLabel value="1" control={<Radio />} label="First Part" />
                    <FormControlLabel value="2" control={<Radio />} label="Second Part" />
                  </RadioGroup>
                  {touched.changingPart && errors.changingPart && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {errors.changingPart}
                    </Typography>)}
                </FormControl>

                <FormControl component="fieldset" margin="normal" fullWidth>
                  <Typography variant="subtitle2">Which part to assign</Typography>
                  <RadioGroup
                    row
                    name="partTimePart"
                    value={values.partTimePart}
                    onChange={handleChange}
                  >
                    <FormControlLabel value="1" control={<Radio />} label="First Part" />
                    <FormControlLabel value="2" control={<Radio />} label="Second Part" />
                  </RadioGroup>
                </FormControl>
              </>
            )}

            {hasChecked && availableDealers.length > 0 && (
              <FormControl fullWidth margin="normal">
                <Typography variant="subtitle1">Select Dealer</Typography>
                <TextField
                  select
                  name="selectedDealerId"
                  value={values.selectedDealerId}
                  onChange={handleChange}
                  SelectProps={{ native: true }}
                  fullWidth
                  error={Boolean(touched.selectedDealerId && errors.selectedDealerId)}
                  helperText={touched.selectedDealerId && errors.selectedDealerId}
                >
                  <option value="">Select dealer</option>
                  {availableDealers.map((dealer) => (
                    <option key={dealer.id} value={dealer.id}>
                      {dealer.name || dealer.email || `Dealer #${dealer.id}`}
                    </option>
                  ))}
                </TextField>
              </FormControl>
            )}

            {hasChecked && !availableDealers.length && (
              <p>There is not any dealer</p>
            )}
          </>
        )}

        {values.actionType === "remove" && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Are you sure you want to remove this shift? This action cannot be undone.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleModalClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={isSubmitting}
        >
          {values.actionType === "change"
            ? hasChecked && availableDealers.length > 0
              ? "Send"
              : "Check"
            : "Remove"}
        </Button>
      </DialogActions>
    </>
  );
};

const ChangeShiftModal = ({
  openAddignModal,
  handleModalClose,
  studios,
  requestedShift,
  shiftDate,
  dealerId,
}) => {
  const dispatch = useDispatch();
  const shiftsStore = useSelector((store) => store.shift);
  const availableDealers = shiftsStore.dealersForChangeShift || [];
  const hasChecked = shiftsStore.hasCheckedDealers;
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [formSuccessMessage, setFormSuccessMessage] = useState("");

  useEffect(() => {
    setFormSuccessMessage("");
  }, [openAddignModal])

  useEffect(() => {
    if (shiftsStore.error.status) {
      setSnackbarMessage(shiftsStore.error.message);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
    if (shiftsStore.success.status) {
      setSnackbarMessage(shiftsStore.success.message);
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    }
  }, [shiftsStore.error.status, shiftsStore.success.status]);

  const handleCloseSnackbar = (_, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackbar(false);
  };

  const validationSchema = Yup.object().shape({
    selectedDate: Yup.date().when("actionType", {
      is: (val) => val === "change",
      then: (schema) => schema.required("Date is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    changingPart: Yup.string().when(["actionType", "shiftMode"], {
      is: (actionType, shiftMode) => actionType === "change" && shiftMode === "2",
      then: (schema) => schema.required("Please select which part to change"),
      otherwise: (schema) => schema.notRequired(),
    }),
    selectedDealerId: Yup.string().when("actionType", {
      is: (val) => val === "change" && hasChecked && availableDealers.length > 0,
      then: (schema) => schema.required("Please select a dealer"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const handleFormikSubmit = async (values, { setSubmitting }) => {
    try {
      if (values.actionType === "change") {
        const formattedDate = format(new Date(values.selectedDate), "dd/MM/yyyy");
        if (hasChecked && availableDealers.length > 0) {
          dispatch(sendShiftChnageRequest({
            selectedShift: values.selectedShift,
            selectedDate: formattedDate,
            changingPart: values.changingPart,
            requestedShift,
            isFull: values.shiftMode,
            partTimePart: values.shiftMode == "2" ? values.partTimePart : null,
            selectedDealerId: values.selectedDealerId
          }))
          setFormSuccessMessage("Shift change request sent successfully!");
        }
        else {
          await dispatch(
            checkFreeDealers({
              selectedShift: values.selectedShift,
              selectedDate: formattedDate,
              requestedShift,
              isFull: values.shiftMode,
              partTimePart: values.shiftMode == "2" ? values.partTimePart : null,
            })
          ).unwrap();
          setSnackbarMessage("Shift availability checked successfully!");
        }
        
        
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      } else if (values.actionType === "remove") {
        await dispatch(dealerChnageShift(requestedShift)).unwrap();
        setFormSuccessMessage("Remove request send successfully");
    //    setSnackbarMessage("Shift removed successfully!");
        // setSnackbarSeverity("success");
        // setOpenSnackbar(true);
        // handleModalClose();
      }
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <>
      <Dialog 
        open={openAddignModal} 
        onClose={handleModalClose} 
        fullWidth maxWidth="sm"
        sx={{zIndex: 999999}}
      >
        <DialogTitle>Shift Change</DialogTitle>
        <Formik
          initialValues={{
            actionType: "change",
            selectedDate: shiftDate || null,
            selectedShift: "1",
            shiftMode: "1",
            partTimePart: "1",
            changingPart: "1",
            selectedDealerId: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleFormikSubmit}
        >
          <ChangeShiftForm
            requestedShift={requestedShift}
            shiftsStore={shiftsStore}
            availableDealers={availableDealers}
            hasChecked={hasChecked}
            handleModalClose={handleModalClose}
            formSuccessMessage={formSuccessMessage}
          />
        </Formik>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
      />
    </>
  );
};

export default ChangeShiftModal;
